import { IconButton, Tooltip, Typography, Link } from '@material-ui/core';
import {
  AccessTime,
  Edit,
  Info,
  RadioButtonUnchecked,
  WarningRounded,
  SwapHoriz,
  HourglassEmpty,
} from '@material-ui/icons';
import { CheckIcon } from 'shared/icons/CheckIcon';
import { formatDate } from '@superdispatch/dates';
import {
  Color,
  ColorDynamic,
  Column,
  Columns,
  Inline,
  Stack,
} from '@superdispatch/ui';
import { TextBox, Box, Button } from '@superdispatch/ui-lab';
import { ReactNode, useMemo } from 'react';
import styled from 'styled-components';
import { castAPIData } from 'shared/api/APISchema';
import { LoadDTO, LoadPaymentDTO, loadPaymentSchema } from '../data/LoadDTO';
import { InstantIcon } from 'shared/icons/InstantIcon';
import {
  InstantTransferStatus,
  OnHoldReasonTooltip,
  StatusInfoTooltip,
  StatusWrapper,
} from 'shared/helpers/PaymentStatusHelpers';
import { useCustomerSupport } from 'shared/helpers/CustomerSupport';
import { useNavigate } from 'react-router-dom';
import { INSTANT_TRANSFER_ACCESS_LOST_HELP_ARTICLE_URL } from 'shared/modules/superpay/instantTransferAccessLostHelpArticleUrl';
import { useSubhaulLoad } from './useSubhaulLoad';
import { ViewLoadSubhauledPaymentStatus } from './ViewLoadSubhaulerPaymentStatuses';

const PaymentStatusWithBorder = styled.div`
  border-left: 1px solid ${ColorDynamic.Silver500};
  margin-left: 7px;
  padding-left: 12px;
`;

const NoWrap = styled.span`
  white-space: nowrap;
`;

function renderStatusDetailsWithTooltip(
  statusDetails: string,
  payment: LoadPaymentDTO,
) {
  if (!payment.status_info) return statusDetails;
  const lastToken = statusDetails.match(/\S+$/)?.[0] ?? '';
  const head = statusDetails.replace(/\s*\S+$/, '');
  return (
    <>
      {head}
      {head && ' '}
      <NoWrap>
        {lastToken}
        <StatusInfoTooltip payment={payment} />
      </NoWrap>
    </>
  );
}

interface PaymentUpdateStatusProps {
  title: string;
  tooltip: string;
}

function PaymentUpdateStatus({ title, tooltip }: PaymentUpdateStatusProps) {
  return (
    <Inline space="xxsmall" verticalAlign="center">
      <SwapHoriz fontSize="small" color="action" />
      <Typography variant="body1">{title}</Typography>
      <Tooltip title={tooltip} arrow={true}>
        <Info fontSize="small" color="action" />
      </Tooltip>
    </Inline>
  );
}

function InstantTransferSettlementBanner({
  payment,
}: {
  payment?: LoadPaymentDTO | null;
}) {
  const navigate = useNavigate();
  const { showLauncherMessanger } = useCustomerSupport();

  switch (payment?.instant_transfer_settlement_status) {
    case 'info':
      return (
        <Box padding="small" borderRadius="small" backgroundColor="Silver200">
          <Stack space="xsmall">
            <Typography>
              Due to Instant Transfer, you are liable that the payment is
              released by the shipper.
            </Typography>
            <Link
              href={INSTANT_TRANSFER_ACCESS_LOST_HELP_ARTICLE_URL}
              target="_blank"
              rel="noreferrer noopener"
            >
              Learn more
            </Link>
          </Stack>
        </Box>
      );
    case 'warning':
      return (
        <Box padding="small" borderRadius="small" backgroundColor="Yellow50">
          <Stack space="xsmall">
            <Typography>
              You are responsible for shipper payment when Instant Transfer is
              used. If payment is not received by{' '}
              {formatDate(payment.instant_transfer_grace_period_end_date, {
                variant: 'Date',
              })}
              , Instant Transfer access may be lost.
            </Typography>
            <Inline space="small" verticalAlign="bottom">
              <Button
                variant="primary"
                size="small"
                onClick={() => {
                  navigate('instant-transfer-settle');
                }}
              >
                Settle Payment
              </Button>
              <Link
                href={INSTANT_TRANSFER_ACCESS_LOST_HELP_ARTICLE_URL}
                target="_blank"
                rel="noreferrer noopener"
              >
                Learn more
              </Link>
            </Inline>
          </Stack>
        </Box>
      );
    case 'past_due':
      return (
        <Box padding="small" borderRadius="small" backgroundColor="Red50">
          <Stack space="xsmall">
            <Typography>
              Access to Instant Transfer Lost. Settle the Payment to regain
              access.
            </Typography>
            <Inline space="small" verticalAlign="bottom">
              <Button
                variant="primary"
                size="small"
                onClick={() => {
                  navigate('instant-transfer-settle');
                }}
              >
                Settle Payment
              </Button>
              <Link
                href={INSTANT_TRANSFER_ACCESS_LOST_HELP_ARTICLE_URL}
                target="_blank"
                rel="noreferrer noopener"
              >
                Learn more
              </Link>
            </Inline>
          </Stack>
        </Box>
      );
    case 'failed':
      return (
        <Box padding="small" borderRadius="small" backgroundColor="Red50">
          <Stack space="xsmall">
            <Typography>
              Last settlement payment failed. Contact Support to settle the
              payment and regain access.
            </Typography>
            <Inline space="small" verticalAlign="bottom">
              <Button
                variant="primary"
                size="small"
                onClick={() => {
                  showLauncherMessanger();
                }}
              >
                Contact Support
              </Button>
              <Link
                href={INSTANT_TRANSFER_ACCESS_LOST_HELP_ARTICLE_URL}
                target="_blank"
                rel="noreferrer noopener"
              >
                Learn more
              </Link>
            </Inline>
          </Stack>
        </Box>
      );
    default:
      return null;
  }
}

interface LoadPaymentStatusesProps {
  load: LoadDTO;
  onEditPayment?: () => void;
  onEditPaidDetails?: () => void;
  onEditMarkAsPaid?: () => void;
  invoiceDetails?: ReactNode;
  paymentDetails?: ReactNode;
  customerPaymentDetails?: ReactNode;
  customerInvoiceDetails?: ReactNode;
  subhaulerInvoiceDetails?: ReactNode;
  subhaulerPaymentDetails?: ReactNode;
  instantTransferDetails?: ReactNode;
}

export function ViewLoadPaymentStatuses({
  load,
  onEditPayment,
  onEditPaidDetails,
  onEditMarkAsPaid,
  invoiceDetails,
  paymentDetails,
  customerPaymentDetails,
  customerInvoiceDetails,
  subhaulerInvoiceDetails,
  subhaulerPaymentDetails,
  instantTransferDetails,
}: LoadPaymentStatusesProps) {
  const { isSubhauled } = useSubhaulLoad();

  const payment = useMemo(
    () =>
      load.payments && load.payments.length > 0
        ? castAPIData(loadPaymentSchema, load.payments[0])
        : null,
    [load.payments],
  );

  const hasInvoiceSent = !!payment?.invoice.sent_at;
  const hasPaymentReceived = !!payment?.paid_at;
  const isOnHold = payment?.status === 'on_hold';
  const isPastDue = payment?.status === 'past_due';
  const isPaymentFailed =
    payment?.status === 'payin_failed' || payment?.status === 'failed';
  const isScheduled = payment?.status === 'scheduled';
  const isProcessing = payment?.status === 'processing';
  const isPending = payment?.status === 'pending';
  const hasStatusDetails = !!payment?.status_details;
  const hasPaymentDue =
    !!payment?.payment_due &&
    hasStatusDetails &&
    !hasPaymentReceived &&
    !isPastDue &&
    !isOnHold &&
    !isPaymentFailed;
  const shouldShowPaymentStatusWithDetails =
    (isScheduled || isPending || isProcessing) && hasStatusDetails;

  const isInstantTransferReceived = !!(
    payment?.instant_transfer_paid_amount &&
    payment?.instant_transfer_fee_amount
  );

  const isInstantTransferInProgress =
    !payment?.instant_transfer_paid_amount &&
    !!payment?.instant_transfer_fee_amount;

  const isPaymentMethodUpdated =
    payment?.original_method != null &&
    load.payment_details?.payment_method != null &&
    load.payment_details?.payment_method !== payment.original_method;

  const isPriceUpdated =
    payment?.original_price != null &&
    payment?.price != null &&
    payment.price !== payment.original_price;

  const invoiceStatusIcon = hasInvoiceSent ? (
    <CheckIcon fontSize="small" htmlColor={ColorDynamic.Green300} />
  ) : (
    <RadioButtonUnchecked fontSize="small" color="action" />
  );

  const invoiceStatusText = hasInvoiceSent
    ? 'Invoice Sent'
    : 'Invoice Not Sent';

  const invoiceStatusTextColor = hasInvoiceSent ? undefined : 'secondary';

  const hasInvoiceDetails = invoiceDetails != null;

  if (isSubhauled) {
    return (
      <ViewLoadSubhauledPaymentStatus
        load={load}
        onEditPayment={onEditPayment}
        onEditMarkAsPaid={onEditMarkAsPaid}
        customerPaymentDetails={customerPaymentDetails}
        subhaulerInvoiceDetails={subhaulerInvoiceDetails}
        customerInvoiceDetails={customerInvoiceDetails}
        subhaulerPaymentDetails={subhaulerPaymentDetails}
      />
    );
  }

  return (
    <Stack space="small">
      {hasPaymentReceived ? (
        <Stack space="xsmall">
          <Columns space="xxsmall" align="center">
            <Column width="fluid">
              <Inline space="xxsmall" verticalAlign="center">
                <CheckIcon fontSize="small" htmlColor={Color.Green300} />
                <TextBox variant="body-semibold">Load Payment Received</TextBox>
                {onEditPaidDetails &&
                  load.payment_details?.payment_method !== 'superpay' && (
                    <IconButton
                      size="small"
                      onClick={onEditPaidDetails}
                      aria-label="Edit payment icon"
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                  )}
              </Inline>
            </Column>
          </Columns>

          {paymentDetails && paymentDetails}
        </Stack>
      ) : (
        <Inline space="xxsmall" verticalAlign="center">
          <RadioButtonUnchecked fontSize="small" color="action" />
          <TextBox color="secondary" variant="body-semibold">
            Load Payment Not Received
          </TextBox>
        </Inline>
      )}

      {shouldShowPaymentStatusWithDetails ? (
        <Stack space="none">
          <StatusWrapper icon={<AccessTime fontSize="small" color="action" />}>
            <TextBox variant="body-semibold">
              {renderStatusDetailsWithTooltip(payment.status_details, payment)}
            </TextBox>
          </StatusWrapper>

          {isProcessing && payment.instant_transfer_available && (
            <PaymentStatusWithBorder>
              <InstantTransferStatus
                loadGUID={load.guid}
                source="Load List"
                buttonVariant="primary"
                icon={InstantIcon}
              />
            </PaymentStatusWithBorder>
          )}
        </Stack>
      ) : (
        hasPaymentDue && (
          <Inline space="xxsmall" verticalAlign="center">
            <AccessTime fontSize="small" color="action" />
            <TextBox variant="body-semibold">
              Payment due on{' '}
              {formatDate(payment.payment_due, { variant: 'Date' })}
            </TextBox>
            {payment.status_info && <StatusInfoTooltip payment={payment} />}
          </Inline>
        )
      )}

      {isPastDue && payment.status_details && (
        <StatusWrapper
          icon={<WarningRounded fontSize="small" htmlColor={Color.Yellow300} />}
        >
          <TextBox color="yellow" variant="body-semibold">
            {renderStatusDetailsWithTooltip(payment.status_details, payment)}
          </TextBox>
        </StatusWrapper>
      )}

      {isOnHold && (
        <StatusWrapper
          icon={<WarningRounded fontSize="small" htmlColor={Color.Yellow300} />}
        >
          <TextBox color="yellow" variant="body-semibold">
            {payment.status_details}
            <OnHoldReasonTooltip payment={payment} />
          </TextBox>
        </StatusWrapper>
      )}

      {isPaymentFailed && payment.status_details && (
        <StatusWrapper
          icon={
            <WarningRounded
              fontSize="small"
              htmlColor={
                payment.status === 'payin_failed'
                  ? Color.Yellow500
                  : Color.Red300
              }
            />
          }
        >
          <TextBox
            color={payment.status === 'payin_failed' ? 'yellow' : 'red'}
            variant="body-semibold"
          >
            {renderStatusDetailsWithTooltip(payment.status_details, payment)}
          </TextBox>
        </StatusWrapper>
      )}

      {isPaymentMethodUpdated && (
        <PaymentUpdateStatus
          title="Payment Method Updated"
          tooltip="Shipper updated the payment method. See details in the activity log."
        />
      )}

      {isPriceUpdated && (
        <PaymentUpdateStatus
          title="Payment Amount Updated"
          tooltip="Shipper updated the price. See details in the activity log."
        />
      )}

      <InstantTransferSettlementBanner payment={payment} />

      {isInstantTransferReceived && (
        <Stack space="xsmall">
          <Columns space="xxsmall" align="center">
            <Column width="fluid">
              <Inline space="xxsmall" verticalAlign="center">
                <InstantIcon fontSize="small" htmlColor={Color.Blue300} />
                <TextBox variant="body-semibold">
                  Instant Transfer Received
                </TextBox>
              </Inline>
            </Column>
          </Columns>

          {!!instantTransferDetails && instantTransferDetails}
        </Stack>
      )}

      {isInstantTransferInProgress && (
        <StatusWrapper
          icon={<HourglassEmpty fontSize="small" htmlColor={Color.Blue300} />}
        >
          <TextBox variant="body-semibold">
            Instant Transfer Processing - Your money is on the way
          </TextBox>
        </StatusWrapper>
      )}

      <Stack space="xsmall">
        <Columns space="xxsmall" align="center">
          <Column width="fluid">
            <Inline space="xxsmall" verticalAlign="center">
              {invoiceStatusIcon}
              <TextBox variant="body-semibold" color={invoiceStatusTextColor}>
                {invoiceStatusText}
              </TextBox>
              {onEditPayment && (
                <IconButton
                  size="small"
                  edge="start"
                  onClick={onEditPayment}
                  aria-label="Edit invoice icon"
                >
                  <Edit fontSize="small" />
                </IconButton>
              )}
            </Inline>
          </Column>
        </Columns>

        {hasInvoiceDetails && invoiceDetails}
      </Stack>
    </Stack>
  );
}
